import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from 'src/app/modules/core/services/http.service';
import { QueryParams, QueryParamsForSelection, QueryResult } from 'src/app/modules/core/store/models/query.model';
import { SecurityGroup } from './security-group.model';
import { SecurityGroupMember } from './security-group-member.model';
import { SecurityGroupPermissionSetItem } from './security-group-permission-set-item.model';
import { User } from '../users';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({ providedIn: 'root' })
export class SecurityGroupApiService {
  constructor(private http: HttpService) {}

  getSecurityGroups(organizationUId: string, params: QueryParams): Observable<QueryResult<SecurityGroup>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups?${queryString}`;

    return this.http.get<QueryResult<SecurityGroup>>(url);
  }

  getSecurityGroupsForSelection(organizationUId: string, params: QueryParamsForSelection): Observable<QueryResult<SecurityGroup>> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/for-selection`;
    return this.http.post<QueryResult<SecurityGroup>>(url, params);
  }

  getSecurityGroupByUId(organizationUId: string, securityGroupUId: string): Observable<SecurityGroup> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroupUId}`;
    return this.http.get(url);
  }

  createSecurityGroup(organizationUId: string, securityGroup: SecurityGroup): Observable<SecurityGroup> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups`;
    return this.http.post(url, securityGroup);
  }

  updateSecurityGroup(organizationUId: string, securityGroup: SecurityGroup): Observable<SecurityGroup> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroup.uId}`;
    return this.http.put(url, securityGroup);
  }

  deleteSecurityGroup(organizationUId: string, securityGroup: SecurityGroup): Observable<void> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroup.uId}`;
    return this.http.delete(url);
  }

  getSecurityGroupPermissionSets(organizationUId: string, securityGroupUId: string): Observable<SecurityGroupPermissionSetItem[]> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroupUId}/permission-sets`;
    return this.http.get(url);
  }

  updateSecurityGroupPermissionSets(
    organizationUId: string,
    securityGroupUId: string,
    items: SecurityGroupPermissionSetItem[]
  ): Observable<SecurityGroupPermissionSetItem[]> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroupUId}/permission-sets`;
    return this.http.put(url, { items });
  }

  getSecurityGroupMembers(
    organizationUId: string,
    securityGroupUId: string,
    params: QueryParams
  ): Observable<QueryResult<SecurityGroupMember>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroupUId}/members?${queryString}`;

    return this.http.get<QueryResult<SecurityGroupMember>>(url);
  }

  addSecurityGroupMembers(organizationUId: string, securityGroupUId: string, users: User[]): Observable<void> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroupUId}/members`;
    return this.http.post(url, { users });
  }

  removeSecurityGroupMember(organizationUId: string, securityGroupUId: string, memberUId: string): Observable<void> {
    const url = `${apiBaseUrl}/v1/iam/organizations/${organizationUId}/security-groups/${securityGroupUId}/members/${memberUId}`;
    return this.http.delete(url);
  }
}
